/* eslint-disable @typescript-eslint/no-explicit-any */
import { EventEmitter, Inject, Injectable, Optional, inject } from '@angular/core';
import { HttpRequest, HttpHandler, type HttpEvent, type HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorReferenceService } from '@odin/odin-core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { UnauthorisedComponent } from '../components/unauthorised/unauthorised.component';
import { CognitoService } from '../services/cognito-service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  public authenticatedEvent: EventEmitter<void> = new EventEmitter<void>();
  private allowlistedHostnames: Set<string> | null = null;
  private errorReference = inject(ErrorReferenceService);

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private cognitoService: CognitoService,
    @Inject('apiRoot') private apiRoot: string,
    @Optional() @Inject('baseAddress') private baseAddress: string | null,
    @Optional() @Inject('xeroBaseAddress') private xeroBaseAddress: string | null,
    @Optional()
    @Inject('notificationWebSocketUrl')
    private notificationWebSocketUrl: string | null,
  ) {}

  InjectToken(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = this.cognitoService.getAccessToken();
    const headers = req.headers.set('Authorization', `Bearer ${token}`);
    const authedReq = req.clone({ headers });
    return next.handle(authedReq);
  }

  RetryRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    if (err.status == 401) {
      if (!this.cognitoService.hasActiveSession()) {
        this.logoutandroute();
      }
      this.HandleUnauth();
    }

    if (err.status === 403) {
      this.errorReference.openErrorSnackbar(
        'You are not authorised to access this resource',
        err,
        'Close',
      );
    }
    return throwError(() => err);
  }

  HandleFail(
    req: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    return next.handle(req);
  }

  private getAllowlistedHostnames(): Set<string> {
    if (this.allowlistedHostnames !== null) {
      return this.allowlistedHostnames;
    }

    const hostnames = [
      this.apiRoot,
      this.baseAddress,
      this.xeroBaseAddress,
      this.notificationWebSocketUrl,
    ]
      .map((endpoint) => this.extractHostname(endpoint))
      .filter((hostname): hostname is string => hostname !== null);

    this.allowlistedHostnames = new Set(hostnames);
    return this.allowlistedHostnames;
  }

  private extractHostname(endpoint: string | null): string | null {
    if (!endpoint) {
      return null;
    }

    const normalizedEndpoint = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(endpoint)
      ? endpoint
      : `https://${endpoint}`;

    try {
      return new URL(normalizedEndpoint).hostname.toLowerCase();
    } catch {
      return null;
    }
  }

  private isAllowlistedHost(url: string): boolean {
    const hostname = this.extractHostname(url);
    if (!hostname) {
      return false;
    }

    return this.getAllowlistedHostnames().has(hostname);
  }

  intercept(
    req: HttpRequest<any>,
    _next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Only attach tokens to explicitly allowlisted API hosts.
    if (
      req.headers.get('EXCLUDETOKEN') === null &&
      this.isAllowlistedHost(req.url)
    ) {
      if (req.headers.get('DONTRETRY') !== null) {
        return from(this.InjectToken(req, _next)).pipe(
          catchError((err: HttpErrorResponse) =>
            this.HandleFail(req, _next, err),
          ),
        );
      } else {
        return from(this.InjectToken(req, _next)).pipe(
          catchError((err: HttpErrorResponse) =>
            this.RetryRequest(req, _next, err),
          ),
        );
      }
    } else {
      return _next.handle(req);
    }
  }

  private HandleUnauth(): void {
    this.dialog
      .open(UnauthorisedComponent, {
        data: {},
        panelClass: 'no-padding',
      })
      .afterClosed()
      .subscribe(() => {
        // TODO: refresh token?
        this.logoutandroute();
      });
  }

  private logoutandroute(): void {
    this.cognitoService.logout();
    this.router.navigate(['/authentication/login']);
  }
}
